import { create } from 'zustand';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import { errorHandler } from '@/utils/functions/errorHandler';
import { toast } from 'react-toastify';
import useCompanyState from './company.state';
import useCompanyProfileCompletionState from './completion.state';

export interface IJobDescription {
  id?: number | string;
  tenant_id?: number | string;
  org_chart_node_id?: number;
  org_chart_node?: {
    id: number;
    name: string;
  };
  org_key_activity_id?: number;
  org_key_activity?: {
    id: number;
    name: string;
  };
  name?: string;
  report?: string;
  frequency?: string;
  kpi?: string;
  kpi_measurement?: string;
  report_file?: any;
  is_published?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface IJobDescriptionPaginatedResponse {
  content: IJobDescription[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

export interface UploadedFileMeta {
  disk: string;
  path: string;
  filename: string;
  original_name: string;
  mime_type: string;
  size: number;
  extension: string;
  url: string;
  uploaded_at: string;
  content_type: string;
}

interface JobDescriptionState {
  jobDescriptions: IJobDescription[];
  paginatedData: IJobDescriptionPaginatedResponse | null;
  selectedJobDescription: IJobDescription | null;
  isLoading: boolean;
  error: unknown;
  jobDescriptionsFull: IJobDescription[];
  getJobDescriptionsFull: (tenantId: string | number) => Promise<void>;
  clearJobDescriptions: () => void;
  createJobDescription: (tenantId: string | number, payload: Partial<IJobDescription>) => Promise<void>;
  getJobDescriptions: (
    tenantId: string | number,
    params?: {
      page?: number;
      page_size?: number;
      org_chart_node_id?: number;
      org_key_activity_id?: number;
      name?: string;
      kpi?: string;
      search?: string;
      published?: boolean;
      orderBy?: string;
      order?: 'asc' | 'desc';
    },
  ) => Promise<IJobDescriptionPaginatedResponse | void>;
  getJobDescriptionDetail: (tenantId: string | number, id: number | string) => Promise<IJobDescription | void>;
  updateJobDescription: (
    tenantId: string | number,
    id: number | string,
    payload: Partial<IJobDescription> | FormData,
  ) => Promise<void>;

  deleteJobDescription: (tenantId: string | number, id: number | string) => Promise<void>;
  bulkPublishJobDescriptions: (tenantId: string | number, isPublished: boolean) => Promise<void>;
  addReportFile: (tenantId: string | number, id: number | string, reportFile: any) => Promise<UploadedFileMeta>;
  addKpiMeasurement: (
    tenantId: string | number,
    id: number | string,
    kpi: string,
    kpiMeasurement: string,
  ) => Promise<void>;
}

const useJobDescriptionState = create<JobDescriptionState>((set, get) => ({
  jobDescriptions: [],
  jobDescriptionsFull: [],
  paginatedData: null,
  selectedJobDescription: null,
  isLoading: false,
  error: null,

  clearJobDescriptions: () => set({ jobDescriptions: [], paginatedData: null }),

  createJobDescription: async (tenantId, payload) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/job-description/${tenantId}`;
      await axiosInstance.post(url, payload);
      await get().getJobDescriptions(tenantId);
      const completionStore = useCompanyProfileCompletionState.getState();
      completionStore.fetchCompletion();
      toast.success('Job Description created successfully');
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  getJobDescriptions: async (tenantId, params) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/job-description/${tenantId}`;
      const { data } = await axiosInstance.get(url, { params });

      const paginatedResponse: IJobDescriptionPaginatedResponse = {
        content: data.data?.content || [],
        totalElements: data.data?.totalElements || 0,
        totalPages: data.data?.totalPages || 0,
        size: data.data?.size || 10,
        number: data.data?.number || 0,
      };

      set({
        jobDescriptions: data.data?.content || [],
        paginatedData: paginatedResponse,
      });

      return paginatedResponse;
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  getJobDescriptionsFull: async tenantId => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/job-description/${tenantId}`;
      const { data } = await axiosInstance.get(url);

      set({
        jobDescriptionsFull: data.data?.content || [],
      });
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  getJobDescriptionDetail: async (tenantId, id) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/job-description/${tenantId}/${id}`;
      const { data } = await axiosInstance.get(url);
      set({ selectedJobDescription: data.data });
      return data.data;
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  updateJobDescription: async (tenantId, id, payload) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/job-description/${tenantId}/${id}`;

      const isFormData = payload instanceof FormData;
      const { data } = await axiosInstance.put(url, payload, {
        headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
      });

      set(state => ({
        jobDescriptions: state.jobDescriptions.map(jd => (jd.id === id ? { ...jd, ...data.data } : jd)),
        selectedJobDescription: data.data,
      }));
      const completionStore = useCompanyProfileCompletionState.getState();
      completionStore.fetchCompletion();
      toast.success('Job Description updated successfully');
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  deleteJobDescription: async (tenantId, id) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/job-description/${tenantId}/${id}`;
      await axiosInstance.delete(url);

      set(state => ({
        jobDescriptions: state.jobDescriptions.filter(jd => jd.id !== id),
        selectedJobDescription: state.selectedJobDescription?.id === id ? null : state.selectedJobDescription,
      }));
      const completionStore = useCompanyProfileCompletionState.getState();
      completionStore.fetchCompletion();

      toast.success('Job Description deleted successfully');
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  bulkPublishJobDescriptions: async (tenantId: string | number, isPublished: boolean) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/job-description/${tenantId}/bulk-publish`;
      await axiosInstance.put(url, { published: isPublished });

      set(state => ({
        jobDescriptions: state.jobDescriptions.map(jd => ({
          ...jd,
          is_published: isPublished,
        })),
      }));

      toast.success(
        isPublished ? 'All Job Descriptions published successfully' : 'All Job Descriptions unpublished successfully',
      );
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  addReportFile: async (tenantId, id, file) => {
    set({ isLoading: true });
    try {
      const { uploadCompanyMedia } = useCompanyState.getState();

      const uploadRes = await uploadCompanyMedia(file, id, 'Report Template');
      const uploadedFile = uploadRes?.data?.[0] || uploadRes?.[0] || uploadRes;

      const formattedFile = {
        disk: uploadedFile.disk || 'minio',
        path: uploadedFile.path,
        filename: uploadedFile.filename,
        original_name: uploadedFile.original_name,
        mime_type: uploadedFile.mime_type,
        size: uploadedFile.size,
        extension: uploadedFile.extension,
        url: uploadedFile.url,
        uploaded_at: uploadedFile.uploaded_at || new Date().toISOString(),
        content_type: uploadedFile.content_type || uploadedFile.mime_type,
      };

      return formattedFile;
    } catch (error) {
      set({ error });
      errorHandler(error);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  addKpiMeasurement: async (tenantId, id, kpi, kpiMeasurement) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/job-description/${tenantId}/${id}`;
      const { data } = await axiosInstance.put(url, {
        kpi,
        kpi_measurement: kpiMeasurement,
      });

      set(state => ({
        jobDescriptions: state.jobDescriptions.map(jd =>
          jd.id === id
            ? {
              ...jd,
              kpi: data.data.kpi,
              kpi_measurement: data.data.kpi_measurement,
            }
            : jd,
        ),
      }));

      toast.success('KPI measurement added successfully');
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },
}));

export default useJobDescriptionState;
